/**
 * Reusable File Menu Component
 * Creates a menu bar with dropdowns
 * Uses CSS classes for easy styling - import components.css or provide your own styles
 */
class FileMenu {
    constructor(config, options = {}) {
        this.activeMenu = null;
        this.config = config;
        this.options = {
            useInlineStyles: false,
            ...options,
        };
        this.element = this.createMenu();
        this.setupEventListeners();
    }
    createMenu() {
        const menuBar = document.createElement('div');
        menuBar.className = `crud-menu-bar ${this.options.className || ''}`.trim();
        // Fallback inline styles if CSS not loaded
        if (this.options.useInlineStyles) {
            menuBar.style.cssText = `
        display: flex;
        background: #f9fafb;
        border-bottom: 1px solid #e5e7eb;
        padding: 0;
        margin: 0;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
        user-select: none;
      `;
        }
        Object.entries(this.config).forEach(([menuName, items]) => {
            const menuContainer = this.createMenuContainer(menuName, items);
            menuBar.appendChild(menuContainer);
        });
        return menuBar;
    }
    createMenuContainer(menuName, items) {
        const container = document.createElement('div');
        container.className = 'crud-menu-container';
        if (this.options.useInlineStyles) {
            container.style.position = 'relative';
        }
        const menuButton = document.createElement('button');
        menuButton.className = 'crud-menu-button';
        menuButton.textContent = menuName;
        menuButton.dataset.menu = menuName;
        if (this.options.useInlineStyles) {
            menuButton.style.cssText = `
        background: none;
        border: none;
        padding: 8px 16px;
        cursor: pointer;
        font-size: 14px;
        color: #374151;
        transition: background 0.2s;
      `;
        }
        const dropdown = this.createDropdown(items);
        dropdown.dataset.menu = menuName;
        container.appendChild(menuButton);
        container.appendChild(dropdown);
        menuButton.addEventListener('click', () => {
            if (this.activeMenu === menuName) {
                this.closeAllMenus();
            }
            else {
                this.closeAllMenus();
                this.openMenu(menuName);
            }
        });
        return container;
    }
    createDropdown(items) {
        const dropdown = document.createElement('div');
        dropdown.className = 'crud-menu-dropdown';
        if (this.options.useInlineStyles) {
            dropdown.style.cssText = `
        display: none;
        position: absolute;
        top: 100%;
        left: 0;
        background: white;
        border: 1px solid #e5e7eb;
        border-radius: 4px;
        box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
        min-width: 200px;
        z-index: 1000;
        padding: 4px 0;
      `;
        }
        items.forEach((item) => {
            if (item.divider) {
                const divider = document.createElement('div');
                divider.className = 'crud-menu-divider';
                if (this.options.useInlineStyles) {
                    divider.style.cssText = `
            height: 1px;
            background: #e5e7eb;
            margin: 4px 0;
          `;
                }
                dropdown.appendChild(divider);
            }
            else {
                const menuItem = document.createElement('button');
                menuItem.className = 'crud-menu-item';
                menuItem.disabled = item.enabled === false;
                // Add icon if provided
                if (item.icon) {
                    const icon = document.createElement('span');
                    icon.className = 'crud-menu-icon';
                    icon.textContent = item.icon;
                    menuItem.appendChild(icon);
                }
                const label = document.createElement('span');
                label.textContent = item.label || '';
                menuItem.appendChild(label);
                if (this.options.useInlineStyles) {
                    menuItem.style.cssText = `
            width: 100%;
            text-align: left;
            background: none;
            border: none;
            padding: 8px 16px;
            cursor: ${item.enabled === false ? 'not-allowed' : 'pointer'};
            font-size: 14px;
            color: ${item.enabled === false ? '#9ca3af' : '#374151'};
            transition: background 0.2s;
            display: flex;
            align-items: center;
            gap: 8px;
          `;
                }
                if (item.enabled !== false) {
                    menuItem.addEventListener('click', () => {
                        if (item.onClick) {
                            item.onClick();
                        }
                        this.closeAllMenus();
                    });
                }
                dropdown.appendChild(menuItem);
            }
        });
        return dropdown;
    }
    setupEventListeners() {
        document.addEventListener('click', (e) => {
            const target = e.target;
            if (!target.closest('.crud-menu-container')) {
                this.closeAllMenus();
            }
        });
    }
    openMenu(menuName) {
        this.activeMenu = menuName;
        const dropdown = this.element.querySelector(`.crud-menu-dropdown[data-menu="${menuName}"]`);
        const button = this.element.querySelector(`.crud-menu-button[data-menu="${menuName}"]`);
        if (dropdown) {
            dropdown.classList.add('open');
            if (this.options.useInlineStyles) {
                dropdown.style.display = 'block';
            }
        }
        if (button) {
            button.classList.add('active');
        }
    }
    closeAllMenus() {
        this.activeMenu = null;
        this.element.querySelectorAll('.crud-menu-dropdown').forEach((dropdown) => {
            dropdown.classList.remove('open');
            if (this.options.useInlineStyles) {
                dropdown.style.display = 'none';
            }
        });
        this.element.querySelectorAll('.crud-menu-button').forEach((button) => {
            button.classList.remove('active');
        });
    }
    /**
     * Get the menu bar element
     */
    getElement() {
        return this.element;
    }
    /**
     * Update menu item enabled state
     */
    setItemEnabled(menuName, itemLabel, enabled) {
        const menu = this.config[menuName];
        if (menu) {
            const item = menu.find((i) => i.label === itemLabel);
            if (item) {
                item.enabled = enabled;
                // Rebuild the menu
                const oldElement = this.element;
                this.element = this.createMenu();
                if (oldElement.parentElement) {
                    oldElement.parentElement.replaceChild(this.element, oldElement);
                }
                this.setupEventListeners();
            }
        }
    }
    /**
     * Add a new menu item to an existing menu
     */
    addMenuItem(menuName, item, position) {
        if (!this.config[menuName]) {
            this.config[menuName] = [];
        }
        if (position !== undefined) {
            this.config[menuName].splice(position, 0, item);
        }
        else {
            this.config[menuName].push(item);
        }
        // Rebuild menu
        const oldElement = this.element;
        this.element = this.createMenu();
        if (oldElement.parentElement) {
            oldElement.parentElement.replaceChild(this.element, oldElement);
        }
        this.setupEventListeners();
    }
    /**
     * Remove a menu item
     */
    removeMenuItem(menuName, itemLabel) {
        if (this.config[menuName]) {
            this.config[menuName] = this.config[menuName].filter((item) => item.label !== itemLabel);
            // Rebuild menu
            const oldElement = this.element;
            this.element = this.createMenu();
            if (oldElement.parentElement) {
                oldElement.parentElement.replaceChild(this.element, oldElement);
            }
            this.setupEventListeners();
        }
    }
    /**
     * Append menu to a container
     */
    appendTo(container) {
        container.appendChild(this.element);
    }
}

/**
 * Reusable Modal Component
 * A flexible modal dialog that can be customized with CSS classes
 */
class Modal {
    constructor(options = {}) {
        this.isOpen = false;
        this.options = {
            closeOnBackdrop: true,
            closeOnEsc: true,
            width: '600px',
            ...options,
        };
        this.backdrop = this.createBackdrop();
        this.element = this.createModal();
        this.contentContainer = this.element.querySelector('.modal-content');
        this.setupEventListeners();
    }
    createBackdrop() {
        const backdrop = document.createElement('div');
        backdrop.className = 'modal-backdrop';
        backdrop.style.cssText = `
      display: none;
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(0, 0, 0, 0.5);
      z-index: 1000;
    `;
        return backdrop;
    }
    createModal() {
        const modal = document.createElement('div');
        modal.className = `modal ${this.options.className || ''}`.trim();
        modal.style.cssText = `
      display: none;
      position: fixed;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      background: white;
      border-radius: 8px;
      box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1), 0 2px 4px rgba(0, 0, 0, 0.06);
      z-index: 1001;
      width: ${this.options.width};
      max-width: 90vw;
      max-height: 90vh;
      overflow: hidden;
      display: flex;
      flex-direction: column;
    `;
        const header = document.createElement('div');
        header.className = 'modal-header';
        header.style.cssText = `
      padding: 20px;
      border-bottom: 1px solid #e5e7eb;
      display: flex;
      justify-content: space-between;
      align-items: center;
    `;
        const title = document.createElement('h2');
        title.className = 'modal-title';
        title.textContent = this.options.title || '';
        title.style.cssText = `
      margin: 0;
      font-size: 1.5rem;
      font-weight: 600;
      color: #111827;
    `;
        const closeBtn = document.createElement('button');
        closeBtn.className = 'modal-close';
        closeBtn.innerHTML = '&times;';
        closeBtn.style.cssText = `
      background: none;
      border: none;
      font-size: 2rem;
      cursor: pointer;
      color: #6b7280;
      padding: 0;
      width: 32px;
      height: 32px;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 4px;
      transition: background 0.2s;
    `;
        closeBtn.onmouseenter = () => closeBtn.style.background = '#f3f4f6';
        closeBtn.onmouseleave = () => closeBtn.style.background = 'none';
        closeBtn.onclick = () => this.close();
        header.appendChild(title);
        header.appendChild(closeBtn);
        const content = document.createElement('div');
        content.className = 'modal-content';
        content.style.cssText = `
      padding: 20px;
      overflow-y: auto;
      flex: 1;
    `;
        modal.appendChild(header);
        modal.appendChild(content);
        return modal;
    }
    setupEventListeners() {
        if (this.options.closeOnBackdrop) {
            this.backdrop.addEventListener('click', () => this.close());
        }
        if (this.options.closeOnEsc) {
            document.addEventListener('keydown', (e) => {
                if (e.key === 'Escape' && this.isOpen) {
                    this.close();
                }
            });
        }
    }
    /**
     * Set the modal title
     */
    setTitle(title) {
        const titleElement = this.element.querySelector('.modal-title');
        if (titleElement) {
            titleElement.textContent = title;
        }
    }
    /**
     * Set the modal content (HTML string or HTMLElement)
     */
    setContent(content) {
        if (typeof content === 'string') {
            this.contentContainer.innerHTML = content;
        }
        else {
            this.contentContainer.innerHTML = '';
            this.contentContainer.appendChild(content);
        }
    }
    /**
     * Get the content container element
     */
    getContentElement() {
        return this.contentContainer;
    }
    /**
     * Open the modal
     */
    open() {
        if (this.isOpen)
            return;
        if (!this.backdrop.parentElement) {
            document.body.appendChild(this.backdrop);
        }
        if (!this.element.parentElement) {
            document.body.appendChild(this.element);
        }
        this.backdrop.style.display = 'block';
        this.element.style.display = 'flex';
        this.isOpen = true;
        if (this.options.onOpen) {
            this.options.onOpen();
        }
    }
    /**
     * Close the modal
     */
    close() {
        if (!this.isOpen)
            return;
        this.backdrop.style.display = 'none';
        this.element.style.display = 'none';
        this.isOpen = false;
        if (this.options.onClose) {
            this.options.onClose();
        }
    }
    /**
     * Toggle modal open/closed
     */
    toggle() {
        if (this.isOpen) {
            this.close();
        }
        else {
            this.open();
        }
    }
    /**
     * Check if modal is currently open
     */
    isModalOpen() {
        return this.isOpen;
    }
    /**
     * Destroy the modal and remove from DOM
     */
    destroy() {
        this.close();
        if (this.backdrop.parentElement) {
            this.backdrop.parentElement.removeChild(this.backdrop);
        }
        if (this.element.parentElement) {
            this.element.parentElement.removeChild(this.element);
        }
    }
}

/**
 * Load Modal Component
 * Displays items from storage with pagination and filtering
 */
class LoadModal {
    constructor(options) {
        this.currentPage = 1;
        this.filterText = '';
        this.totalPages = 1;
        this.totalItems = 0;
        this.storage = options.storage;
        this.options = {
            itemsPerPage: 10,
            ...options,
        };
        this.modal = new Modal({
            title: 'Load from Local Storage',
            width: '700px',
            className: 'load-modal',
        });
    }
    /**
     * Open the modal and load items
     */
    async open() {
        await this.render();
        this.modal.open();
    }
    /**
     * Close the modal
     */
    close() {
        this.modal.close();
    }
    /**
     * Render the modal content
     */
    async render() {
        const container = document.createElement('div');
        container.className = 'load-modal-container';
        // Search/Filter section
        const filterSection = this.createFilterSection();
        container.appendChild(filterSection);
        // Items list
        const itemsList = await this.createItemsList();
        container.appendChild(itemsList);
        // Pagination
        const pagination = this.createPagination();
        container.appendChild(pagination);
        this.modal.setContent(container);
    }
    createFilterSection() {
        const section = document.createElement('div');
        section.className = 'filter-section';
        section.style.cssText = `
      margin-bottom: 20px;
    `;
        const input = document.createElement('input');
        input.type = 'text';
        input.placeholder = 'Search items...';
        input.value = this.filterText;
        input.className = 'filter-input';
        input.style.cssText = `
      width: 100%;
      padding: 10px 12px;
      border: 1px solid #d1d5db;
      border-radius: 4px;
      font-size: 14px;
      transition: border-color 0.2s;
    `;
        input.onfocus = () => input.style.borderColor = '#3b82f6';
        input.onblur = () => input.style.borderColor = '#d1d5db';
        input.addEventListener('input', (e) => {
            this.filterText = e.target.value;
            this.currentPage = 1;
            this.render();
        });
        section.appendChild(input);
        return section;
    }
    async createItemsList() {
        const container = document.createElement('div');
        container.className = 'items-list';
        container.style.cssText = `
      max-height: 400px;
      overflow-y: auto;
      border: 1px solid #e5e7eb;
      border-radius: 4px;
      margin-bottom: 20px;
    `;
        try {
            // Get items with pagination
            const result = await this.storage.list({
                page: this.currentPage,
                limit: this.options.itemsPerPage || 10,
                filter: this.filterText
                    ? (item) => {
                        const searchText = this.filterText.toLowerCase();
                        return Object.values(item).some((val) => String(val).toLowerCase().includes(searchText));
                    }
                    : undefined,
            });
            this.totalPages = result.totalPages;
            this.totalItems = result.total;
            if (result.items.length === 0) {
                container.innerHTML = `
          <div style="padding: 40px; text-align: center; color: #6b7280;">
            No items found
          </div>
        `;
                return container;
            }
            result.items.forEach((item) => {
                const itemElement = this.createItemElement(item);
                container.appendChild(itemElement);
            });
        }
        catch (error) {
            container.innerHTML = `
        <div style="padding: 40px; text-align: center; color: #ef4444;">
          Error loading items: ${error instanceof Error ? error.message : 'Unknown error'}
        </div>
      `;
        }
        return container;
    }
    createItemElement(item) {
        const element = document.createElement('div');
        element.className = 'item-row';
        element.style.cssText = `
      padding: 12px 16px;
      border-bottom: 1px solid #e5e7eb;
      cursor: pointer;
      transition: background 0.2s;
      display: flex;
      justify-content: space-between;
      align-items: center;
    `;
        element.onmouseenter = () => element.style.background = '#f9fafb';
        element.onmouseleave = () => element.style.background = 'none';
        const content = document.createElement('div');
        content.style.cssText = `
      flex: 1;
      overflow: hidden;
    `;
        if (this.options.formatItem) {
            content.innerHTML = this.options.formatItem(item);
        }
        else if (this.options.displayFields) {
            const fields = this.options.displayFields
                .map((field) => `<strong>${String(field)}:</strong> ${item[field]}`)
                .join(' | ');
            content.innerHTML = fields;
        }
        else {
            content.innerHTML = `<pre style="margin: 0; font-size: 12px; white-space: pre-wrap;">${JSON.stringify(item, null, 2)}</pre>`;
        }
        const loadButton = document.createElement('button');
        loadButton.textContent = 'Load';
        loadButton.className = 'load-item-btn';
        loadButton.style.cssText = `
      background: #3b82f6;
      color: white;
      border: none;
      padding: 6px 16px;
      border-radius: 4px;
      cursor: pointer;
      font-size: 14px;
      transition: background 0.2s;
      margin-left: 12px;
    `;
        loadButton.onmouseenter = () => loadButton.style.background = '#2563eb';
        loadButton.onmouseleave = () => loadButton.style.background = '#3b82f6';
        loadButton.addEventListener('click', (e) => {
            e.stopPropagation();
            if (this.options.onItemSelect) {
                this.options.onItemSelect(item);
            }
            this.close();
        });
        element.appendChild(content);
        element.appendChild(loadButton);
        // Also allow clicking the row itself
        element.addEventListener('click', () => {
            if (this.options.onItemSelect) {
                this.options.onItemSelect(item);
            }
            this.close();
        });
        return element;
    }
    createPagination() {
        const container = document.createElement('div');
        container.className = 'pagination';
        container.style.cssText = `
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding-top: 12px;
      border-top: 1px solid #e5e7eb;
    `;
        const info = document.createElement('div');
        info.style.cssText = `
      color: #6b7280;
      font-size: 14px;
    `;
        const start = (this.currentPage - 1) * (this.options.itemsPerPage || 10) + 1;
        const end = Math.min(this.currentPage * (this.options.itemsPerPage || 10), this.totalItems);
        info.textContent = `Showing ${start}-${end} of ${this.totalItems} items`;
        const controls = document.createElement('div');
        controls.style.cssText = `
      display: flex;
      gap: 8px;
    `;
        const prevButton = document.createElement('button');
        prevButton.textContent = 'Previous';
        prevButton.disabled = this.currentPage === 1;
        prevButton.style.cssText = `
      padding: 6px 12px;
      border: 1px solid #d1d5db;
      background: ${this.currentPage === 1 ? '#f3f4f6' : 'white'};
      color: ${this.currentPage === 1 ? '#9ca3af' : '#374151'};
      border-radius: 4px;
      cursor: ${this.currentPage === 1 ? 'not-allowed' : 'pointer'};
      font-size: 14px;
      transition: background 0.2s;
    `;
        if (this.currentPage > 1) {
            prevButton.onmouseenter = () => prevButton.style.background = '#f9fafb';
            prevButton.onmouseleave = () => prevButton.style.background = 'white';
        }
        prevButton.addEventListener('click', () => {
            if (this.currentPage > 1) {
                this.currentPage--;
                this.render();
            }
        });
        const pageInfo = document.createElement('span');
        pageInfo.style.cssText = `
      padding: 6px 12px;
      color: #374151;
      font-size: 14px;
    `;
        pageInfo.textContent = `Page ${this.currentPage} of ${this.totalPages}`;
        const nextButton = document.createElement('button');
        nextButton.textContent = 'Next';
        nextButton.disabled = this.currentPage === this.totalPages;
        nextButton.style.cssText = `
      padding: 6px 12px;
      border: 1px solid #d1d5db;
      background: ${this.currentPage === this.totalPages ? '#f3f4f6' : 'white'};
      color: ${this.currentPage === this.totalPages ? '#9ca3af' : '#374151'};
      border-radius: 4px;
      cursor: ${this.currentPage === this.totalPages ? 'not-allowed' : 'pointer'};
      font-size: 14px;
      transition: background 0.2s;
    `;
        if (this.currentPage < this.totalPages) {
            nextButton.onmouseenter = () => nextButton.style.background = '#f9fafb';
            nextButton.onmouseleave = () => nextButton.style.background = 'white';
        }
        nextButton.addEventListener('click', () => {
            if (this.currentPage < this.totalPages) {
                this.currentPage++;
                this.render();
            }
        });
        controls.appendChild(prevButton);
        controls.appendChild(pageInfo);
        controls.appendChild(nextButton);
        container.appendChild(info);
        container.appendChild(controls);
        return container;
    }
    /**
     * Destroy the modal
     */
    destroy() {
        this.modal.destroy();
    }
}

/**
 * Prompt Modal Component
 * Simple modal for getting text input from user
 */
class PromptModal {
    constructor(options) {
        this.inputElement = null;
        this.resolveCallback = null;
        this.options = {
            confirmText: 'OK',
            cancelText: 'Cancel',
            ...options,
        };
        this.modal = new Modal({
            title: this.options.title,
            width: '400px',
            className: 'prompt-modal',
            closeOnBackdrop: false,
            closeOnEsc: true,
            onClose: () => this.handleCancel(),
        });
        this.render();
    }
    render() {
        const container = document.createElement('div');
        container.className = 'prompt-container';
        // Message
        const message = document.createElement('p');
        message.textContent = this.options.message;
        message.style.cssText = `
      margin: 0 0 16px 0;
      color: #374151;
      font-size: 14px;
    `;
        // Input
        this.inputElement = document.createElement('input');
        this.inputElement.type = 'text';
        this.inputElement.placeholder = this.options.placeholder || '';
        this.inputElement.value = this.options.defaultValue || '';
        this.inputElement.style.cssText = `
      width: 100%;
      padding: 10px 12px;
      border: 1px solid #d1d5db;
      border-radius: 4px;
      font-size: 14px;
      margin-bottom: 20px;
      box-sizing: border-box;
      transition: border-color 0.2s;
    `;
        this.inputElement.onfocus = () => {
            if (this.inputElement) {
                this.inputElement.style.borderColor = '#3b82f6';
            }
        };
        this.inputElement.onblur = () => {
            if (this.inputElement) {
                this.inputElement.style.borderColor = '#d1d5db';
            }
        };
        // Handle Enter key
        this.inputElement.addEventListener('keydown', (e) => {
            if (e.key === 'Enter') {
                this.handleConfirm();
            }
        });
        // Buttons
        const buttons = document.createElement('div');
        buttons.style.cssText = `
      display: flex;
      justify-content: flex-end;
      gap: 8px;
    `;
        const cancelButton = document.createElement('button');
        cancelButton.textContent = this.options.cancelText;
        cancelButton.style.cssText = `
      padding: 8px 16px;
      border: 1px solid #d1d5db;
      background: white;
      color: #374151;
      border-radius: 4px;
      cursor: pointer;
      font-size: 14px;
      transition: background 0.2s;
    `;
        cancelButton.onmouseenter = () => cancelButton.style.background = '#f9fafb';
        cancelButton.onmouseleave = () => cancelButton.style.background = 'white';
        cancelButton.addEventListener('click', () => this.handleCancel());
        const confirmButton = document.createElement('button');
        confirmButton.textContent = this.options.confirmText;
        confirmButton.style.cssText = `
      padding: 8px 16px;
      border: none;
      background: #3b82f6;
      color: white;
      border-radius: 4px;
      cursor: pointer;
      font-size: 14px;
      transition: background 0.2s;
    `;
        confirmButton.onmouseenter = () => confirmButton.style.background = '#2563eb';
        confirmButton.onmouseleave = () => confirmButton.style.background = '#3b82f6';
        confirmButton.addEventListener('click', () => this.handleConfirm());
        buttons.appendChild(cancelButton);
        buttons.appendChild(confirmButton);
        container.appendChild(message);
        container.appendChild(this.inputElement);
        container.appendChild(buttons);
        this.modal.setContent(container);
    }
    handleConfirm() {
        const value = this.inputElement?.value || '';
        if (this.resolveCallback) {
            this.resolveCallback(value);
            this.resolveCallback = null;
        }
        this.modal.close();
    }
    handleCancel() {
        if (this.resolveCallback) {
            this.resolveCallback(null);
            this.resolveCallback = null;
        }
        this.modal.close();
    }
    /**
     * Open the modal and get user input
     * Returns a promise that resolves with the input value or null if cancelled
     */
    prompt() {
        return new Promise((resolve) => {
            this.resolveCallback = resolve;
            this.modal.open();
            // Focus input after modal opens
            setTimeout(() => {
                this.inputElement?.focus();
                this.inputElement?.select();
            }, 100);
        });
    }
    /**
     * Destroy the modal
     */
    destroy() {
        this.modal.destroy();
    }
}

/**
 * File utility functions for importing and exporting data
 */
/**
 * Load data from a JSON file
 */
function loadFromFile() {
    return new Promise((resolve) => {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = '.json';
        input.style.display = 'none';
        input.addEventListener('change', async (e) => {
            const file = e.target.files?.[0];
            if (!file) {
                resolve(null);
                return;
            }
            try {
                const text = await file.text();
                const data = JSON.parse(text);
                resolve(data);
            }
            catch (error) {
                console.error('Error reading file:', error);
                alert('Error reading file: ' + (error instanceof Error ? error.message : 'Unknown error'));
                resolve(null);
            }
        });
        input.addEventListener('cancel', () => {
            resolve(null);
        });
        document.body.appendChild(input);
        input.click();
        document.body.removeChild(input);
    });
}
/**
 * Export data to a JSON file
 */
function exportToFile(data, filename) {
    try {
        const json = JSON.stringify(data, null, 2);
        const blob = new Blob([json], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename.endsWith('.json') ? filename : `${filename}.json`;
        a.style.display = 'none';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    }
    catch (error) {
        console.error('Error exporting file:', error);
        alert('Error exporting file: ' + (error instanceof Error ? error.message : 'Unknown error'));
    }
}
/**
 * Download text as a file
 */
function downloadTextFile(content, filename, mimeType = 'text/plain') {
    try {
        const blob = new Blob([content], { type: mimeType });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        a.style.display = 'none';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    }
    catch (error) {
        console.error('Error downloading file:', error);
        alert('Error downloading file: ' + (error instanceof Error ? error.message : 'Unknown error'));
    }
}

/**
 * FileMenuBuilder - Helper to create file menus with built-in CRUD operations
 * Provides ready-to-use Load/Save/Export functionality
 */
class FileMenuBuilder {
    constructor(options) {
        this.menuConfig = {};
        this.options = {
            isLoggedIn: false,
            itemsPerPage: 10,
            ...options,
        };
    }
    /**
     * Add built-in File menu with Load/Save operations
     */
    withFileMenu() {
        this.menuConfig['File'] = [
            {
                label: 'Load from Storage',
                icon: '📂',
                enabled: true,
                onClick: () => this.handleLoadFromStorage(),
            },
            {
                label: 'Load (Remote)',
                icon: '☁️',
                enabled: this.options.isLoggedIn,
            },
            {
                label: 'Load from Disk',
                icon: '💾',
                enabled: true,
                onClick: () => this.handleLoadFromDisk(),
            },
            { divider: true },
            {
                label: 'Save (Remote)',
                icon: '☁️',
                enabled: this.options.isLoggedIn,
            },
            {
                label: 'Save to Storage',
                icon: '💾',
                enabled: true,
                onClick: () => this.handleSaveToStorage(),
            },
            {
                label: 'Export to File',
                icon: '📤',
                enabled: true,
                onClick: () => this.handleExportToFile(),
            },
        ];
        return this;
    }
    /**
     * Add custom menu
     */
    withMenu(menuName, items) {
        this.menuConfig[menuName] = items;
        return this;
    }
    /**
     * Add Edit menu with common actions
     */
    withEditMenu(callbacks) {
        this.menuConfig['Edit'] = [];
        if (callbacks.onClear) {
            this.menuConfig['Edit'].push({
                label: 'Clear',
                icon: '🗑️',
                enabled: true,
                onClick: callbacks.onClear,
            });
        }
        if (callbacks.onCopy) {
            this.menuConfig['Edit'].push({
                label: 'Copy',
                icon: '📋',
                enabled: true,
                onClick: callbacks.onCopy,
            });
        }
        if (callbacks.onPaste) {
            this.menuConfig['Edit'].push({
                label: 'Paste',
                icon: '📄',
                enabled: true,
                onClick: callbacks.onPaste,
            });
        }
        return this;
    }
    /**
     * Add Help menu
     */
    withHelpMenu(callbacks) {
        this.menuConfig['Help'] = [];
        if (callbacks.onDocumentation) {
            this.menuConfig['Help'].push({
                label: 'Documentation',
                icon: '📖',
                enabled: true,
                onClick: callbacks.onDocumentation,
            });
        }
        if (callbacks.onAbout) {
            if (callbacks.onDocumentation) {
                this.menuConfig['Help'].push({ divider: true });
            }
            this.menuConfig['Help'].push({
                label: 'About',
                icon: 'ℹ️',
                enabled: true,
                onClick: callbacks.onAbout,
            });
        }
        return this;
    }
    /**
     * Build and return the FileMenu
     */
    build() {
        return new FileMenu(this.menuConfig);
    }
    // Built-in handlers
    async handleLoadFromStorage() {
        const loadModal = new LoadModal({
            storage: this.options.storage,
            itemsPerPage: this.options.itemsPerPage,
            displayFields: this.options.displayFields,
            formatItem: this.options.formatItem,
            onItemSelect: (item) => {
                if (this.options.onItemLoad) {
                    this.options.onItemLoad(item);
                }
                this.setStatus(`Loaded: ${item.name || 'item'}`);
            },
        });
        await loadModal.open();
    }
    async handleLoadFromDisk() {
        const data = await loadFromFile();
        if (data) {
            if (this.options.onItemLoad) {
                this.options.onItemLoad(data);
            }
            this.setStatus('Loaded from disk');
        }
    }
    async handleSaveToStorage() {
        if (!this.options.getCurrentItem) {
            this.setStatus('No getCurrentItem callback provided', true);
            return;
        }
        const currentData = this.options.getCurrentItem();
        const promptModal = new PromptModal({
            title: 'Save to Storage',
            message: 'Enter a name for this item:',
            placeholder: 'Item name',
            defaultValue: currentData.name || '',
        });
        const name = await promptModal.prompt();
        promptModal.destroy();
        if (!name) {
            this.setStatus('Save cancelled');
            return;
        }
        try {
            const itemData = {
                ...currentData,
                name,
            };
            if (currentData.id) {
                await this.options.storage.update(currentData.id, itemData);
                this.setStatus('Item updated successfully');
            }
            else {
                const created = await this.options.storage.create({
                    ...itemData,
                    id: Date.now(),
                });
                if (this.options.onItemLoad) {
                    this.options.onItemLoad(created);
                }
                this.setStatus('Item saved successfully');
            }
        }
        catch (error) {
            this.setStatus('Error saving: ' + (error instanceof Error ? error.message : 'Unknown error'), true);
        }
    }
    async handleExportToFile() {
        if (!this.options.getCurrentItem) {
            this.setStatus('No getCurrentItem callback provided', true);
            return;
        }
        const currentData = this.options.getCurrentItem();
        const promptModal = new PromptModal({
            title: 'Export to File',
            message: 'Enter filename:',
            placeholder: 'filename',
            defaultValue: currentData.name || 'export',
        });
        const filename = await promptModal.prompt();
        promptModal.destroy();
        if (!filename) {
            this.setStatus('Export cancelled');
            return;
        }
        exportToFile(currentData, filename);
        this.setStatus(`Exported to ${filename}.json`);
    }
    setStatus(message, isError = false) {
        if (this.options.onStatusChange) {
            this.options.onStatusChange(message, isError);
        }
        else {
            console.log(message);
        }
    }
}

export { FileMenu, FileMenuBuilder, downloadTextFile, exportToFile, loadFromFile };
//# sourceMappingURL=filemenu.esm.js.map
